import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../services/api';

export interface ProjectPhase {
  _id: string;
  project: string;
  phaseName: string;
  phaseOrder: number;
  status: 'Not Started' | 'In Progress' | 'Complete';
  entryCount: number;
  updatedAt: string;
  createdAt: string;
}

export interface PhaseEntry {
  _id: string;
  phase: string;
  project: string;
  addedBy: { _id: string; name: string; email: string };
  notes: string;
  media: { contentType: string }[];
  mediaCount: number;
  mediaUrls: string[];
  postedToTracking: boolean;
  createdAt: string;
}

interface ProjectPhasesState {
  phases: ProjectPhase[];
  entries: { [phaseId: string]: PhaseEntry[] };
  phasesLoading: boolean;
  entriesLoading: { [phaseId: string]: boolean };
  error: string | null;
}

const initialState: ProjectPhasesState = {
  phases: [],
  entries: {},
  phasesLoading: false,
  entriesLoading: {},
  error: null,
};

export const fetchPhases = createAsyncThunk(
  'projectPhases/fetchPhases',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/project-phases/${projectId}`);
      return response.data as ProjectPhase[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch phases');
    }
  }
);

export const updatePhaseStatus = createAsyncThunk(
  'projectPhases/updateStatus',
  async ({ phaseId, status }: { phaseId: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/project-phases/${phaseId}/status`, { status });
      return response.data as ProjectPhase;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update status');
    }
  }
);

export const fetchPhaseEntries = createAsyncThunk(
  'projectPhases/fetchEntries',
  async (phaseId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/project-phases/${phaseId}/entries`);
      return { phaseId, entries: response.data as PhaseEntry[] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch entries');
    }
  }
);

export const createPhaseEntry = createAsyncThunk(
  'projectPhases/createEntry',
  async (
    {
      phaseId,
      notes,
      files,
      postToTracking,
    }: { phaseId: string; notes: string; files: File[]; postToTracking: boolean },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('notes', notes);
      formData.append('postToTracking', postToTracking.toString());
      files.forEach((file) => formData.append('media', file));

      const response = await api.post(`/project-phases/${phaseId}/entries`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { phaseId, entry: response.data as PhaseEntry };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create entry');
    }
  }
);

export const editPhaseEntry = createAsyncThunk(
  'projectPhases/editEntry',
  async ({ phaseId, entryId, notes }: { phaseId: string; entryId: string; notes: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/project-phases/${phaseId}/entries/${entryId}`, { notes });
      return { phaseId, entry: response.data as PhaseEntry };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to edit entry');
    }
  }
);

export const deletePhaseEntry = createAsyncThunk(
  'projectPhases/deleteEntry',
  async ({ phaseId, entryId }: { phaseId: string; entryId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/project-phases/${phaseId}/entries/${entryId}`);
      return { phaseId, entryId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete entry');
    }
  }
);

const projectPhasesSlice = createSlice({
  name: 'projectPhases',
  initialState,
  reducers: {
    clearPhases: (state) => {
      state.phases = [];
      state.entries = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhases.pending, (state) => {
        state.phasesLoading = true;
        state.error = null;
      })
      .addCase(fetchPhases.fulfilled, (state, action: PayloadAction<ProjectPhase[]>) => {
        state.phasesLoading = false;
        state.phases = action.payload;
      })
      .addCase(fetchPhases.rejected, (state, action) => {
        state.phasesLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePhaseStatus.fulfilled, (state, action: PayloadAction<ProjectPhase>) => {
        const index = state.phases.findIndex((p) => p._id === action.payload._id);
        if (index !== -1) state.phases[index] = { ...state.phases[index], ...action.payload };
      })
      .addCase(updatePhaseStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(fetchPhaseEntries.pending, (state, action) => {
        state.entriesLoading[action.meta.arg] = true;
      })
      .addCase(fetchPhaseEntries.fulfilled, (state, action: PayloadAction<{ phaseId: string; entries: PhaseEntry[] }>) => {
        state.entriesLoading[action.payload.phaseId] = false;
        state.entries[action.payload.phaseId] = action.payload.entries;
      })
      .addCase(fetchPhaseEntries.rejected, (state, action) => {
        state.entriesLoading[action.meta.arg] = false;
        state.error = action.payload as string;
      })
      .addCase(createPhaseEntry.fulfilled, (state, action: PayloadAction<{ phaseId: string; entry: PhaseEntry }>) => {
        const { phaseId, entry } = action.payload;
        if (!state.entries[phaseId]) state.entries[phaseId] = [];
        state.entries[phaseId].push(entry);
        const phase = state.phases.find((p) => p._id === phaseId);
        if (phase) phase.entryCount = (phase.entryCount || 0) + 1;
      })
      .addCase(createPhaseEntry.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(editPhaseEntry.fulfilled, (state, action: PayloadAction<{ phaseId: string; entry: PhaseEntry }>) => {
        const { phaseId, entry } = action.payload;
        const list = state.entries[phaseId];
        if (list) {
          const index = list.findIndex((e) => e._id === entry._id);
          if (index !== -1) list[index] = entry;
        }
      })
      .addCase(deletePhaseEntry.fulfilled, (state, action: PayloadAction<{ phaseId: string; entryId: string }>) => {
        const { phaseId, entryId } = action.payload;
        if (state.entries[phaseId]) {
          state.entries[phaseId] = state.entries[phaseId].filter((e) => e._id !== entryId);
        }
        const phase = state.phases.find((p) => p._id === phaseId);
        if (phase && phase.entryCount > 0) phase.entryCount -= 1;
      });
  },
});

export const { clearPhases } = projectPhasesSlice.actions;
export default projectPhasesSlice.reducer;
